// Header.js - Modern Design (pairs with Sidebar.js)

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import Logout from '@mui/icons-material/Logout';
import CallIcon from '@mui/icons-material/Call';
import { MdEmail } from "react-icons/md";
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BadgeIcon from '@mui/icons-material/Badge';
import { FiMenu, FiChevronDown } from "react-icons/fi";
import { clearReduxForHomeComponent } from '../redux/actions/home_action';
import { clearReduxForUserComponent } from '../redux/actions/user_actions';

const ACCENT_BLUE = '#22638f';
const TEXT_MUTED = '#93a0b5';
const TEXT_BODY = '#3b4a63';
const BORDER = '#eef2f8';

function Header({ pageTitle, onMenuClick }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [organizationName, setOrganizationName] = useState(
    localStorage.getItem('company_name') || ''
  );
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const userName = localStorage.getItem("user_name") || "No Name";
  const email = localStorage.getItem("email");
  const phone = localStorage.getItem("phone_number");
  const role = localStorage.getItem("role_name") || "User";
  const location = localStorage.getItem("location_name") || "UNKNOWN";
  const photoURL = localStorage.getItem("photoURL");

  useEffect(() => {
    const handleStorageChange = () => {
      setOrganizationName(localStorage.getItem('company_name') || '');
    };
    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const logout = async () => {
    localStorage.clear();
    dispatch(clearReduxForHomeComponent());
    dispatch(clearReduxForUserComponent());
    navigate("/");
  };

  return (
    <header
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px 28px',
        backgroundColor: '#eef4fb',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        <button
          type="button"
          aria-label="Menu"
          onClick={onMenuClick}
          style={{
            width: 40,
            height: 40,
            borderRadius: '10px',
            border: `1px solid ${BORDER}`,
            backgroundColor: '#fff',
            color: TEXT_MUTED,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            fontSize: 18,
            flexShrink: 0,
          }}
        >
          <FiMenu />
        </button>
        <div>
          <p style={{ margin: 0, fontSize: 18, fontWeight: 700, color: TEXT_BODY }}>
            {organizationName}
          </p>
          {pageTitle && (
            <p style={{ margin: '2px 0 0', fontSize: 12.5, color: TEXT_MUTED }}>
              {pageTitle}
            </p>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Tooltip title="Profile">
          <button
            type="button"
            onClick={handleClick}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '4px 6px',
              borderRadius: '10px',
            }}
          >
            <Avatar
              src={photoURL || undefined}
              sx={{
                width: 36,
                height: 36,
                fontSize: 15,
                fontWeight: 700,
                backgroundColor: ACCENT_BLUE,
              }}
            >
              {userName.charAt(0).toUpperCase()}
            </Avatar>
            <div style={{ textAlign: 'left' }}>
              <p style={{ margin: 0, fontSize: 13.5, fontWeight: 700, color: TEXT_BODY, whiteSpace: 'nowrap' }}>
                {userName}
              </p>
              <p style={{ margin: 0, fontSize: 11.5, color: TEXT_MUTED }}>
                {role}
              </p>
            </div>
            <FiChevronDown style={{ color: TEXT_MUTED, fontSize: 14 }} />
          </button>
        </Tooltip>

        <Menu
          anchorEl={anchorEl}
          id="account-menu"
          open={open}
          onClose={handleClose}
          onClick={handleClose}
          PaperProps={{
            elevation: 0,
            sx: {
              overflow: 'visible',
              borderRadius: '14px',
              boxShadow: '0 8px 32px rgba(12, 61, 110, 0.14)',
              border: `1px solid ${BORDER}`,
              mt: 1.5,
              '&:before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                borderTop: `1px solid ${BORDER}`,
                borderLeft: `1px solid ${BORDER}`,
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexDirection: 'column',
              minWidth: 250,
              gap: '10px',
              textAlign: 'center',
              padding: '20px 16px 16px',
            }}
          >
            <Avatar
              sx={{
                width: 52,
                height: 52,
                fontSize: 20,
                fontWeight: 700,
                backgroundColor: ACCENT_BLUE,
              }}
            >
              {userName.charAt(0).toUpperCase()}
            </Avatar>

            <p style={{ margin: 0, fontSize: 15, fontWeight: 700, color: TEXT_BODY, maxWidth: 220, wordBreak: 'break-word', whiteSpace: 'normal' }}>
              {userName}
            </p>

            {email ? (
              <span style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', gap: 6, maxWidth: 220, wordBreak: 'break-word', textAlign: 'left', fontSize: 15, color: TEXT_MUTED }}>
                <MdEmail style={{ width: 15, height: 15, marginTop: 1, flexShrink: 0, color: ACCENT_BLUE }} />
                <span>{email}</span>
              </span>
            ) : phone ? (
              <span style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-start', gap: 6, maxWidth: 220, wordBreak: 'break-word', textAlign: 'left', fontSize: 15, color: TEXT_MUTED }}>
                <CallIcon style={{ width: 15, height: 15, marginTop: 1, flexShrink: 0, color: ACCENT_BLUE }} />
                <span>{phone}</span>
              </span>
            ) : null}

            {role && (
              <span style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', gap: 6, maxWidth: 220, wordBreak: 'break-word', textAlign: 'left', fontSize: 15, color: TEXT_MUTED }}>
                <BadgeIcon style={{ width: 15, height: 15, marginTop: 1, flexShrink: 0, color: ACCENT_BLUE }} />
                <span>{role}</span>
              </span>
            )}

            {location && (
              <span style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', gap: 6, maxWidth: 220, wordBreak: 'break-word', textAlign: 'left', fontSize: 15, color: TEXT_MUTED }}>
                <LocationOnIcon style={{ width: 15, height: 15, marginTop: 1, flexShrink: 0, color: ACCENT_BLUE }} />
                <span>{location}</span>
              </span>
            )}
          </Box>

          <Divider sx={{ borderColor: BORDER, mt: 1 }} />

          <MenuItem
            onClick={logout}
            sx={{
              fontSize: 13.5,
              fontWeight: 700,
              color: TEXT_BODY,
              padding: '12px 20px',
              '&:hover': { backgroundColor: '#f8fbfe' },
            }}
          >
            <ListItemIcon>
              <Logout fontSize="small" sx={{ color: ACCENT_BLUE }} />
            </ListItemIcon>
            Logout
          </MenuItem>
        </Menu>
      </div>
    </header>
  );
}

export default Header;